import { Link } from "react-router-dom";
import { Mail, Phone, ArrowUpRight, Instagram, Facebook, Linkedin } from "lucide-react";
import { SITE } from "../config/site";
import { trackEvent } from "../lib/analytics";

const SERVICES = [
  { to: "/hajj-marketing", label: "Hajj Marketing" },
  { to: "/umrah-marketing", label: "Umrah Marketing" },
  { to: "/services/meta-ads", label: "Meta Ads" },
  { to: "/services/google-ads", label: "Google Ads" },
  { to: "/services/social-media-marketing", label: "Social Media Marketing" },
  { to: "/services/whatsapp-marketing", label: "WhatsApp Marketing" },
  { to: "/services/email-marketing", label: "Email Marketing" },
  { to: "/services/website-development", label: "Website Development" },
  { to: "/services/on-ground-content-production", label: "Content Production" },
];

const SOCIALS = [
  { icon: Instagram, url: () => SITE.socials.instagram, label: "Instagram", testid: "footer-social-instagram" },
  { icon: Facebook, url: () => SITE.socials.facebook, label: "Facebook", testid: "footer-social-facebook" },
  { icon: Linkedin, url: () => SITE.socials.linkedin, label: "LinkedIn", testid: "footer-social-linkedin" },
];

const COMPANY = [
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resource Hub" },
  { to: "/how-it-works", label: "Our Process" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const LEGAL = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms" },
  { to: "/cookie-policy", label: "Cookie Policy" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Col = ({ title, links, prefix }) => {
  return (
    <div>
      <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-6">
        {title}
      </h4>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              data-testid={`footer-link-${prefix}-${slug(l.label)}`}
              className="text-sm text-cream/60 hover:text-cream transition-colors duration-300"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-charcoal relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 geo-pattern-light opacity-[0.04]" />
      <div className="relative max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pb-16 border-b border-cream/10">
          <div className="max-w-xl">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/assets/nexus-logo-bg.png"
                alt="Nexus Catalyst Agency"
                className="h-16 w-16"
                data-testid="footer-logo"
              />
              <span className="text-lg font-bold tracking-tight text-cream">
                Nexus Catalyst <span className="text-tan">Agency</span>
              </span>
            </div>
            <p className="text-2xl md:text-3xl font-bold tracking-tight text-cream leading-snug">
              {"Specialized growth marketing for India's Hajj & Umrah travel businesses."}
            </p>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email-link"
              className="mt-6 inline-flex items-center gap-2 text-sm text-cream/60 hover:text-gold transition-colors"
            >
              <Mail size={15} strokeWidth={1.5} className="text-gold" />
              {SITE.email}
            </a>
            <a
              href={`tel:${SITE.phone}`}
              data-testid="footer-phone-link"
              className="mt-3 inline-flex items-center gap-2 text-sm text-cream/60 hover:text-gold transition-colors"
            >
              <Phone size={15} strokeWidth={1.5} className="text-gold" />
              {SITE.phoneDisplay}
            </a>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) =>
                s.url() ? (
                  <a
                    key={s.label}
                    href={s.url()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="flex h-10 w-10 items-center justify-center border border-cream/20 text-cream/60 hover:border-gold hover:text-gold transition-colors"
                  >
                    <s.icon size={16} strokeWidth={1.5} />
                  </a>
                ) : (
                  <span
                    key={s.label}
                    aria-label={`${s.label} (link coming soon)`}
                    data-testid={s.testid}
                    className="flex h-10 w-10 items-center justify-center border border-cream/10 text-cream/25"
                  >
                    <s.icon size={16} strokeWidth={1.5} />
                  </span>
                )
              )}
            </div>
          </div>
          <Link
            to={"/contact"}
            data-testid="footer-book-call-button"
            onClick={() => trackEvent("cta_click", { cta: "book_strategy_call", placement: "footer" })}
            className="group inline-flex items-center gap-2 bg-tan text-cream text-sm font-semibold tracking-wide px-8 py-4 hover:bg-gold hover:text-charcoal transition-colors duration-300 shrink-0"
          >
            {"Book a Strategy Call"}
            <ArrowUpRight
              size={16}
              strokeWidth={1.5}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 py-14">
          <Col title="Services" links={SERVICES} prefix="services" />
          <Col title="Company" links={COMPANY} prefix="company" />
          <Col title="Legal" links={LEGAL} prefix="legal" />
        </div>

        <div className="pt-8 border-t border-cream/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-cream/40" data-testid="footer-copyright">
            {"© Nexus Catalyst Agency. All rights reserved."}
          </p>
          <p className="text-xs text-cream/40">
            {"Built for India's Hajj & Umrah Travel Market."}
          </p>
        </div>
      </div>
    </footer>
  );
};
